import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, DollarSign, Star } from 'lucide-react';

interface ActivityNotification {
  id: string;
  type: 'booking' | 'earning' | 'review';
  message: string;
  location?: string;
  time: string;
  avatar?: string;
}

const activities: ActivityNotification[] = [
  { id: '1', type: 'booking', message: 'Sarah from New York just booked a session', location: 'New York', time: '2m ago' },
  { id: '2', type: 'booking', message: 'Michael from London just booked a session', location: 'London', time: '8m ago' },
  { id: '3', type: 'review', message: 'Emma left a 5-star review', time: '12m ago' },
  { id: '4', type: 'booking', message: 'Lisa from Toronto just booked a session', location: 'Toronto', time: '15m ago' },
  { id: '5', type: 'booking', message: 'Alex from Sydney just booked a session', location: 'Sydney', time: '22m ago' },
  { id: '6', type: 'review', message: 'David left a 5-star review', time: '25m ago' },
  { id: '7', type: 'booking', message: 'Maria from Madrid just booked a session', location: 'Madrid', time: '30m ago' },
  { id: '8', type: 'review', message: 'Sophie left a 5-star review', time: '35m ago' },
];

export const RealTimeSocialProof = () => {
  const [currentActivity, setCurrentActivity] = useState<ActivityNotification | null>(null);
  const [activityIndex, setActivityIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const showInterval = setInterval(() => {
      setIsVisible(true);
      setCurrentActivity(activities[activityIndex]);
      setActivityIndex((prev) => (prev + 1) % activities.length);

      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    }, 8000);

    return () => clearInterval(showInterval);
  }, [activityIndex]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'review':
        return <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 pointer-events-none">
      <AnimatePresence>
        {isVisible && currentActivity && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: -20 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: 20, x: -20 }}
            className="bg-background border-2 border-primary/20 rounded-lg shadow-2xl p-4 max-w-sm pointer-events-auto"
          >
            <div className="flex items-start gap-3">
              <Avatar className="h-10 w-10 border-2 border-primary/20">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {currentActivity.message.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {getIcon(currentActivity.type)}
                  <span className="text-xs font-medium text-muted-foreground">
                    {currentActivity.time}
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground leading-snug">
                  {currentActivity.message}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
